import React, { useState } from "react";
import Nav from "../../components/Home/Nav";
import Footer from "../../components/Home/Footer";
import { Fade } from "react-awesome-reveal";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import {
  HiMail,
  HiPhone,
  HiLocationMarker,
  HiChevronDown,
  HiArrowRight,
  HiChatAlt2,
  HiCamera,
  HiX,
  HiPhotograph,
} from "react-icons/hi";
import { compressImage } from "../../utils/imageCompressor";

function Contact() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    description: "",
  });

  const [images, setImages] = useState([]);
  const [isCompressing, setIsCompressing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = () => {
    setIsOpen(!isOpen);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "phone") {
      // Restrict phone number input to numbers and optional leading +
      const numericValue = value.replace(/[^0-9+]/g, "");
      setFormData((prevState) => ({
        ...prevState,
        phone: numericValue,
      }));
      return;
    }
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;

    if (images.length + files.length > 5) {
      toast.warning("You can attach up to 5 images per message.");
    }

    setIsCompressing(true);

    try {
      const remainingSlots = 5 - images.length;
      const filesToProcess = files.slice(0, remainingSlots);

      const processedImages = await Promise.all(
        filesToProcess.map((file) => compressImage(file, 300 * 1024)),
      );

      const validImages = processedImages.filter(Boolean);
      setImages((prev) => [...prev, ...validImages]);
    } catch (err) {
      console.error("Compression error:", err);
      toast.error("Failed to process attached images");
    } finally {
      setIsCompressing(false);
    }
  };

  const removeImage = (indexToRemove) => {
    setImages((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const BASE_URL = `${process.env.REACT_APP_API_URL || `${process.env.REACT_APP_API_URL || "http://localhost:5000"}`}/api/`;

  const handleSubmit = async (event) => {
    event.preventDefault();
    let newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Full name required";
    if (!formData.email.trim()) newErrors.email = "Valid email required";
    if (!formData.phone.trim()) newErrors.phone = "Contact number required";
    if (!formData.description.trim())
      newErrors.description = "Please enter your message";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = {
        ...formData,
        images,
      };

      const response = await axios.post(`${BASE_URL}createContact`, payload);
      if (response.status === 201) {
        toast.success("Message and attachments submitted successfully!");
        navigate("/");
      } else {
        toast.error(response.data.message || "Submission failed");
      }
    } catch (error) {
      const errMsg =
        error.response?.data?.message || "Network submission failed";
      toast.error(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      <Nav />

      {/* Map Section */}
      <section className="relative group pt-32">
        <div
          className={`relative w-full overflow-hidden transition-all duration-700 ease-in-out ${isOpen ? "h-[70vh]" : "h-96"}`}
        >
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d113032.65322787261!2d85.24373135767756!3d27.708935957714655!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39eb198a307baabf%3A0xb5137c1bf18db1ea!2sKathmandu%2044600!5e0!3m2!1sen!2snp!4v1707492932344!5m2!1sen!2snp"
            className="w-full h-full border-none filter contrast-125 saturate-50 hover:saturate-100 transition-all duration-1000 grayscale group-hover:grayscale-0"
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>

          <div className="absolute inset-0 bg-gradient-to-t from-slate-50 via-transparent to-transparent pointer-events-none"></div>

          <button
            onClick={handleOpen}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-md px-6 py-3 rounded-2xl shadow-2xl border border-white flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-900 hover:bg-blue-600 hover:text-white transition-all active:scale-95"
          >
            <HiLocationMarker className={isOpen ? "rotate-180" : ""} />{" "}
            {isOpen ? "Collapse Map" : "Expand Location"}
          </button>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Contact Details */}
          <div className="lg:col-span-5 space-y-12">
            <Fade direction="left" triggerOnce>
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-full text-[10px] font-black uppercase tracking-[0.2em] mb-6 border border-blue-100">
                  <HiChatAlt2 /> Inquiries
                </div>
                <h1 className="text-4xl md:text-5xl font-black text-slate-900 leading-tight mb-6">
                  Let's Discuss Your{" "}
                  <span className="text-blue-600">Enterprise</span> Solutions
                </h1>
                <p className="text-slate-500 font-medium leading-relaxed max-w-md">
                  Have a complex waterproofing requirement? Our technical
                  engineers are standing by to architect your structural
                  defense.
                </p>
              </div>

              <div className="space-y-6">
                {[
                  {
                    icon: HiPhone,
                    label: "Phone Support",
                    value: "+977 9801037595",
                    color: "text-blue-600",
                    bg: "bg-blue-50",
                  },
                  {
                    icon: HiMail,
                    label: "Email Support",
                    value: "sdoffice13@gmail.com",
                    color: "text-emerald-600",
                    bg: "bg-emerald-50",
                  },
                  {
                    icon: HiLocationMarker,
                    label: "Company Location",
                    value: "Naya Baneshwor, Kathmandu",
                    color: "text-indigo-600",
                    bg: "bg-indigo-50",
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-5 group cursor-pointer"
                  >
                    <div
                      className={`w-14 h-14 ${item.bg} ${item.color} rounded-2xl flex items-center justify-center transition-all group-hover:scale-110 shadow-sm border border-white group-hover:shadow-lg`}
                    >
                      <item.icon size={24} />
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">
                        {item.label}
                      </p>
                      <p className="font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                        {item.value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Fade>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-7">
            <Fade direction="up" triggerOnce>
              <div className="glass rounded-[3rem] p-8 md:p-12 border border-white shadow-2xl shadow-blue-200/20 bg-white/40 backdrop-blur-xl">
                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                        Full Name
                      </label>
                      <input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your Full Name"
                        className={`w-full bg-white/50 border ${errors.name ? "border-red-300" : "border-slate-200"} rounded-2xl px-6 py-4 font-bold text-slate-700 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all outline-none`}
                      />
                      {errors.name && (
                        <p className="text-[10px] font-bold text-red-500 uppercase tracking-tight ml-1">
                          {errors.name}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                        Your Email
                      </label>
                      <input
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Your Email Address"
                        className={`w-full bg-white/50 border ${errors.email ? "border-red-300" : "border-slate-200"} rounded-2xl px-6 py-4 font-bold text-slate-700 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all outline-none`}
                      />
                      {errors.email && (
                        <p className="text-[10px] font-bold text-red-500 uppercase tracking-tight ml-1">
                          {errors.email}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                      Phone Number
                    </label>
                    <input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="98XXXXXXXX"
                      className={`w-full bg-white/50 border ${errors.phone ? "border-red-300" : "border-slate-200"} rounded-2xl px-6 py-4 font-bold text-slate-700 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all outline-none`}
                    />
                    {errors.phone && (
                      <p className="text-[10px] font-bold text-red-500 uppercase tracking-tight ml-1">
                        {errors.phone}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                      Describe your problem
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Describe your requirements or inquiry details..."
                      className={`w-full bg-white/50 border ${errors.description ? "border-red-300" : "border-slate-200"} rounded-2xl px-6 py-5 font-bold text-slate-700 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all outline-none h-36 resize-none`}
                    />
                    {errors.description && (
                      <p className="text-[10px] font-bold text-red-500 uppercase tracking-tight ml-1">
                        {errors.description}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between ml-1">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                        Attach Reference Images (Optional)
                      </label>
                      <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md">
                        Auto-compressed &lt; 300KB
                      </span>
                    </div>

                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                      id="contact-images-input"
                      disabled={isCompressing || images.length >= 5}
                    />

                    <label
                      htmlFor="contact-images-input"
                      className={`w-full flex flex-col items-center justify-center p-6 border-2 border-dashed border-slate-200 hover:border-blue-500 rounded-2xl cursor-pointer bg-white/50 hover:bg-blue-50/20 transition-all group ${
                        isCompressing ? "opacity-60 cursor-not-allowed" : ""
                      }`}
                    >
                      <HiCamera className="text-slate-400 group-hover:text-blue-600 text-3xl mb-2 transition-colors" />
                      <span className="text-xs font-bold text-slate-700 group-hover:text-blue-600">
                        {isCompressing
                          ? "Processing & Compressing Images..."
                          : "Click or drag to attach reference photos"}
                      </span>
                      <span className="text-[10px] text-slate-400 mt-1">
                        Up to 5 images • Automatic canvas compression under
                        300KB per image
                      </span>
                    </label>

                    {/* Image Thumbnail Grid */}
                    {images.length > 0 && (
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 pt-2">
                        {images.map((img, idx) => (
                          <div
                            key={idx}
                            className="relative group rounded-xl overflow-hidden border border-slate-200 aspect-square bg-slate-100 shadow-sm"
                          >
                            <img
                              src={img}
                              alt={`Attachment ${idx + 1}`}
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(idx)}
                              className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full text-xs shadow-md opacity-90 hover:opacity-100 transition-opacity"
                              title="Remove image"
                            >
                              <HiX size={12} />
                            </button>
                            <span className="absolute bottom-1 left-1 bg-slate-900/80 text-white text-[8px] font-bold px-1.5 py-0.5 rounded backdrop-blur-sm">
                              &lt;300KB
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isCompressing || isSubmitting}
                    className="w-full group py-5 bg-slate-900 text-white rounded-[2rem] font-black uppercase tracking-[0.2em] text-xs hover:bg-blue-600 transition-all shadow-xl hover:shadow-blue-200 active:scale-[0.98] flex items-center justify-center gap-3 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span>Submitting Inquiries...</span>
                      </>
                    ) : isCompressing ? (
                      <span>Compressing Images...</span>
                    ) : (
                      <>
                        <span>Submit</span>
                        <HiArrowRight className="group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </Fade>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default Contact;
